import { redirect } from '@tanstack/react-router'

import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { toast } from 'sonner'

import { isTokenProviderReady, setGlobalGetToken } from '@/lib/auth'
import { isDemoPresentation } from '@/lib/constants'
import { clearQueryCache } from '@/lib/react-query'
import { captureException } from '@/lib/sentry/ui-error-reporter'

import { userQueries } from '@/shared/auth/queries'
import { tenantSessionQueries } from '@/shared/auth/tenant-session'
import {
	getCurrentTenant,
	resetCurrentTenant,
	setCurrentTenant,
} from '@/shared/auth/tenant-store'

import { getCurrentRole } from '../store'
import { runSignOutTeardowns } from './sign-out-teardown'
import { hasRequiredRole, isSSOUser } from './user'

import type { Role } from '../types'
import type { Clerk, UseAuthReturn, UseUserReturn } from '@clerk/shared/types'
import type { QueryClient } from '@tanstack/react-query'
import type { ParsedLocation } from '@tanstack/react-router'

/**
 * Checks if the user is authenticated.
 * Redirects to login if not signed in.
 * @param auth - The auth object from Clerk
 * @param location - Current parsed location for redirect param
 */
export const requireAuthGuard = ({
	auth,
	location,
}: {
	auth: UseAuthReturn | null
	location?: Pick<ParsedLocation, 'href'>
}) => {
	if (!auth?.isSignedIn) {
		throw redirect({
			to: '/login',
			replace: true,
			search: {
				redirect: location ? location.href : undefined,
			},
		})
	}
}

/**
 * Redirect to unauthorized if the user does not have the required role
 * @param requiredRole - The required role
 */
export const roleAuthGuard = (requiredRole: Role) => {
	const role = getCurrentRole()

	if (role !== undefined && !hasRequiredRole(role, requiredRole)) {
		throw redirect({ to: '/unauthorized' })
	}
}

/**
 * Sign out: clears Clerk session, react-query cache, localStorage, PostHog, Sentry
 * @param clerk - The Clerk instance
 */
export const signOut = async ({ clerk }: { clerk: Clerk }) => {
	await clerk?.signOut()

	await clearQueryCache()

	resetCurrentTenant()

	// Clean up legacy user store key from localStorage
	localStorage.removeItem('@bitlyft/user')

	// Feature-owned localStorage (dashboard drafts today) dies with the session —
	// the two named keys above are not the whole of what a session leaves behind on
	// a shared workstation. Features register their own teardown, so nothing here
	// has to know what they store (A-02).
	runSignOutTeardowns()

	posthog.reset()

	Sentry.setUser(null)
}

/**
 * Seeds the dashboard session: validates auth, fetches user/tenant data, inits analytics.
 *
 * **The ONLY place in the codebase that switches `currentTenant`.** Any
 * tenant change in the URL (`?t=`) flows through here. Sign-out may clear
 * tenant state, but callers must never switch tenants by calling
 * `setCurrentTenant` directly — see `knowledge/reference/tenant-switching.md`
 * for the rationale.
 *
 * Called from `_dashboard.tsx` `beforeLoad`, which orchestrates the
 * surrounding flow (loader, cache clear, post-load redirect).
 *
 * @param clerk - The Clerk context from the router
 * @param queryClient - TanStack Query client
 * @param location - Current parsed location
 * @param search - Parsed search params from the route; `search.t` (the
 *   AIR tenant id from the URL) takes precedence when validating against
 *   `allowed_tenants`.
 * @param prefetch - Extra warm-ups to run alongside the tenant details, given
 *   the resolved tenant. Auth owns the *bootstrap order* but must not name a
 *   feature's data layer (A-02), so the caller starts the fetches and the
 *   route — which may import features (A-01) — is the one that knows them.
 *   Invoked inside the same `Promise.all`, so this stays one round trip.
 *
 * @see knowledge/reference/tenant-switching.md
 */
export const seedDashboardSession = async ({
	clerk,
	queryClient,
	location,
	search,
	prefetch,
}: {
	clerk: {
		user: UseUserReturn | null
		auth: UseAuthReturn | null
	}
	queryClient: QueryClient
	location: Pick<ParsedLocation, 'href'>
	search?: { t?: string }
	prefetch?: (tenant: string) => Array<Promise<unknown>>
}) => {
	// 1. Session check — Clerk loaded but session invalid/expired
	if (clerk.auth?.isLoaded && !clerk.auth?.isSignedIn) {
		toast.error('Your session has expired. Please log in again.')

		captureException(new Error('Session not signed in during beforeLoad'), {
			source: 'router',
			action: 'check_signed_in',
			feature: 'auth',
		})

		throw redirect({ to: '/logout', replace: true })
	}

	// 2. Auth guard
	requireAuthGuard({ auth: clerk.auth, location })

	// 3. Token provider init
	if (!isTokenProviderReady() && clerk.auth?.getToken) {
		setGlobalGetToken(clerk.auth.getToken)
	}

	// 4. TOTP enforcement — non-OAuth users must have TOTP enabled
	const clerkUser = clerk.user?.user
	if (clerkUser && !isSSOUser(clerkUser) && !clerkUser.totpEnabled) {
		throw redirect({ to: '/setup-totp', replace: true })
	}

	// 5. Fetch /me (auto-provisions user if needed)
	let meData

	try {
		meData = await queryClient.ensureQueryData({
			...userQueries.me(),
			revalidateIfStale: true,
		})
	} catch (error) {
		captureException(
			error instanceof Error ? error : new Error('/me fetch failed'),
			{
				source: 'router',
				action: 'fetch_me',
				feature: 'auth',
			},
		)

		throw error
	}

	// 6. Resolve tenant — URL ?t= param takes precedence for cross-tenant links
	let tenant = getCurrentTenant()
	if (
		search?.t &&
		meData.allowed_tenants.some((allowed) => allowed.id === search.t)
	) {
		tenant = search.t
		setCurrentTenant(tenant)
	} else if (
		!tenant ||
		!meData.allowed_tenants.some((allowed) => allowed.id === tenant)
	) {
		tenant = meData.user.tenant ?? undefined
		if (tenant) setCurrentTenant(tenant)
	}

	if (tenant) {
		const resolvedTenant = tenant
		await Promise.all([
			queryClient.ensureQueryData({
				...tenantSessionQueries.details(resolvedTenant),
				revalidateIfStale: true,
			}),
			...(prefetch?.(resolvedTenant) ?? []),
		])
	}

	// 7. Analytics
	try {
		const tenantDetails = tenant
			? queryClient.getQueryData(tenantSessionQueries.details(tenant).queryKey)
			: undefined

		Sentry.setUser({
			id: meData.user.uname ?? undefined,
			email: meData.user.uname,
			tenant,
			role: tenant ? meData.tenant_roles[tenant] : undefined,
			sku: tenantDetails?.sku_info?.sku_name,
		})
	} catch {
		Sentry.setUser({
			id: meData.user.uname ?? undefined,
			tenant,
		})
	}
	// Not in the demo: every demo visitor gets the same stubbed user, so
	// identifying would merge all real prospects into one useless
	// mock.user@example.com person. Left anonymous, `person_profiles:
	// 'identified_only'` never promotes them; demo traffic is separated by the
	// `demo_mode` super property, not by person.
	if (meData.user.uname && !isDemoPresentation) {
		posthog.identify(meData.user.uname, { tenant })
	}
}
